import { NgClass, NgForOf, NgIf } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Button } from 'primeng/button';
import { Divider } from 'primeng/divider';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { AuthStore } from '../../../../auth/services/auth.store';
import { TimesheetEntryDto, WorkItemDto } from '../../../../api';
import { TimesheetCellClickEvent } from '../../model/timesheet-cell-click-event';
import { TimesheetCell } from '../../model/timesheet-cell';
import { TimesheetRow } from '../../model/timesheet-row';
import { WeekDay } from '../../model/week-day';

@Component({
  selector: 'app-timesheet-table',
  standalone: true,
  imports: [
    TableModule,
    NgForOf,
    NgClass,
    FormsModule,
    NgIf,
    Button,
    Select,
    Tooltip,
    Divider,
    RouterLink,
  ],
  templateUrl: './timesheet-table.component.html',
  styleUrl: './timesheet-table.component.css',
})
export class TimesheetTableComponent implements OnInit, OnChanges {
  private readonly authStore = inject(AuthStore);

  @Input() timeEntries: TimesheetEntryDto[] = [];
  @Input() workItems: WorkItemDto[] = [];
  @Input() startDate!: Date;
  @Output() rowDelete = new EventEmitter<TimesheetRow>();
  @Output() cellClick = new EventEmitter<TimesheetCellClickEvent>();

  readonly isOwner = this.authStore.isOwner;

  weekDays: WeekDay[] = [];
  entriesByProject: TimesheetRow[] = [];
  availableWorkItems: WorkItemDto[] = [];

  ngOnChanges(changes: SimpleChanges) {
    if (changes['startDate'] && this.startDate) {
      this.weekDays = this.buildWeek(this.startDate);
    }

    if (changes['timeEntries'] || changes['startDate'] || changes['workItems']) {
      this.refreshEntries();
    }
  }

  ngOnInit() {}

  private buildWeek(start: Date): WeekDay[] {
    const weekdays = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nd'];
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);

      const dayName = weekdays[d.getDay() === 0 ? 6 : d.getDay() - 1];
      const dateLabel = d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });

      return {
        date: d,
        label: `${dayName} ${dateLabel}`,
      };
    });
  }

  private groupEntries(): TimesheetRow[] {
    if (this.timeEntries.length === 0) {
      return [];
    }

    const projects = [...new Set(this.timeEntries.map((t) => t.workItemId))];

    return projects.map((workItemId) => {
      const entriesForProject = this.weekDays.map((day) => {
        const dayKey = this.toLocalDateKey(day.date);
        const entriesForDay = this.timeEntries.filter(
          (t) => t.workItemId === workItemId && this.toLocalDateKey(new Date(t.start!)) === dayKey,
        );

        if (entriesForDay.length === 0) {
          return {
            workItemId,
            date: this.toLocalDateKey(day.date),
            duration: 0,
            inputValue: null,
            isTemp: true,
            entries: [],
            entryCount: 0,
          } as TimesheetCell;
        }

        const totalDuration = entriesForDay.reduce((acc, entry) => acc + (entry.duration ?? 0), 0);

        return {
          ...entriesForDay[0],
          duration: totalDuration,
          date: this.toLocalDateKey(day.date),
          isTemp: false,
          entries: entriesForDay,
          entryCount: entriesForDay.length,
        } as TimesheetCell;
      });

      const workItem = this.workItems.find((w) => w.id === workItemId);
      return {
        workItemId,
        workItemName: workItem?.name,
        color: workItem?.color,
        clientName: workItem?.clientName,
        billingMode: entriesForProject[0]?.billingMode ?? workItem?.billingMode ?? null,
        isTemp: false,
        timeEntries: entriesForProject,
      };
    });
  }

  private refreshEntries(): void {
    this.entriesByProject = this.groupEntries();
    this.rebuildAvailableWorkItems();
    this.ensureEmptyRowAtEnd();
  }

  private ensureEmptyRowAtEnd(): void {
    if (this.availableWorkItems.length === 0) {
      return;
    }

    const lastRow = this.entriesByProject[this.entriesByProject.length - 1];
    if (!lastRow || !lastRow.isTemp || lastRow.workItemId) {
      this.entriesByProject.push(this.getEmptyRow());
    }
  }

  private getEmptyRow(): TimesheetRow {
    return {
      workItemId: null,
      workItemName: undefined,
      clientName: undefined,
      billingMode: undefined,
      isTemp: true,
      timeEntries: this.weekDays.map((day) => ({
        date: this.toLocalDateKey(day.date),
        duration: 0,
        inputValue: this.dateAtLocalMidnight(day.date),
        isTemp: true,
        entries: [],
        entryCount: 0,
      })) as TimesheetCell[],
    };
  }

  protected onProjectSelected(event: { value: string }, row: TimesheetRow) {
    const workItemId = event.value;
    if (!workItemId) {
      return;
    }

    row.workItemId = workItemId;

    const selectedProject = this.workItems.find((w) => w.id === workItemId);
    if (!selectedProject) {
      return;
    }

    row.workItemName = selectedProject?.name;
    row.color = selectedProject?.color;
    row.clientName = selectedProject?.clientName;
    row.billingMode = selectedProject?.billingMode ?? null;
    row.isTemp = false;

    row.timeEntries = row.timeEntries.map((entry, index) => {
      if (!entry.isTemp) return entry;
      return {
        workItemId,
        date: this.toLocalDateKey(this.weekDays[index].date),
        duration: 0,
        inputValue: this.dateAtLocalMidnight(this.weekDays[index].date),
        isTemp: false,
        entries: [],
        entryCount: 0,
      };
    });

    this.rebuildAvailableWorkItems();
    this.ensureEmptyRowAtEnd();
  }

  addEmptyRow() {
    if (this.availableWorkItems.length === 0) {
      return;
    }

    this.entriesByProject.push(this.getEmptyRow());
  }

  protected removeRow(row: TimesheetRow, rowIndex: number) {
    const ids = this.collectRowEntryIds(row);
    const hasRowAnyTimeEntry = ids.length !== 0;

    if (row.isTemp || !hasRowAnyTimeEntry) {
      this.entriesByProject.splice(rowIndex, 1);
    }

    this.rebuildAvailableWorkItems();
    this.ensureEmptyRowAtEnd();

    if (!row.isTemp && hasRowAnyTimeEntry) {
      this.rowDelete.emit(row);
    }
  }

  private collectRowEntryIds(row: TimesheetRow): string[] {
    return row.timeEntries.flatMap((cell) =>
      (cell.entries ?? (cell.id ? [cell] : [])).map((entry) => entry.id!).filter(Boolean),
    );
  }

  protected openCell(row: TimesheetRow, cell: TimesheetCell, dayIndex: number): void {
    if (!row.workItemId) {
      return;
    }

    this.cellClick.emit({
      row,
      cell,
      dayIndex,
    });
  }

  private dateAtLocalMidnight(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  }

  private rebuildAvailableWorkItems() {
    const usedIds = this.entriesByProject
      .filter((r) => !r.isTemp && r.workItemId != null)
      .map((r) => r.workItemId!)
      .filter((id): id is string => id != null);

    this.availableWorkItems = this.workItems.filter((wi) => !usedIds.includes(wi.id!));
  }

  protected formatMinutesForInput(minutes: number | null): string {
    if (minutes == null) {
      return '';
    }
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }

  protected formatBillingMode(mode?: string | null): string {
    switch (mode) {
      case 'HOURLY_RATE':
        return 'Godzinowy';
      case 'REVENUE_THRESHOLD':
        return 'Progowy';
      default:
        return '';
    }
  }

  protected getRowTotalMinutes(projectRow: TimesheetRow): number {
    return projectRow.timeEntries.reduce((acc, te) => acc + (te?.duration ?? 0), 0);
  }

  protected getDayTotalMinutes(dayIndex: number): number {
    return this.entriesByProject.reduce((acc, row) => {
      const entry = row.timeEntries[dayIndex];
      return acc + (entry?.duration ?? 0);
    }, 0);
  }

  protected getWeekTotalMinutes(): number {
    return this.entriesByProject.reduce((acc, row) => acc + this.getRowTotalMinutes(row), 0);
  }

  private toLocalDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

